'use client';

import { useCallback, useSyncExternalStore } from 'react';
import { getBrowserWindow } from '@/lib/utils/browser-window';

const getMatchMedia = () => {
  const browserWindow = getBrowserWindow();
  if (!browserWindow || typeof browserWindow.matchMedia !== 'function') {
    return;
  }
  return browserWindow.matchMedia.bind(browserWindow);
};

function canUseMatchMedia(): boolean {
  return getMatchMedia() !== undefined;
}

export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (listener: () => void) => {
      if (!canUseMatchMedia()) {
        return () => {
          // No matchMedia support; nothing to clean up.
        };
      }

      const matchMedia = getMatchMedia();
      if (!matchMedia) {
        return () => {
          // matchMedia unavailable; nothing to clean up.
        };
      }

      const mediaQueryList = matchMedia(query);
      // Modern browsers all expose `addEventListener('change', …)` on MediaQueryList.
      // We intentionally drop the legacy addListener/removeListener path to simplify the hook;
      // see ENG-3492 for the official browser support list.
      const handleChange = () => {
        listener();
      };

      mediaQueryList.addEventListener('change', handleChange);
      return () => {
        mediaQueryList.removeEventListener('change', handleChange);
      };
    },
    [query]
  );

  const getSnapshot = useCallback(() => {
    const matchMedia = getMatchMedia();
    if (!matchMedia) {
      return false;
    }
    return matchMedia(query).matches;
  }, [query]);

  const getServerSnapshot = useCallback(() => false, []);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
